import { type Marker, MarkerClusterer } from "@googlemaps/markerclusterer";
import { useQuery, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { AdvancedMarker, Map as GoogleMap, useMap } from "@vis.gl/react-google-maps";
import { type ActionLog, ActionLogSchemas } from "@workspace/contracts/logs";
import { Button } from "@workspace/ui/components/button";
import { Card, CardContent, CardHeader, CardTitle } from "@workspace/ui/components/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@workspace/ui/components/table";
import { Tooltip, TooltipContent, TooltipTrigger } from "@workspace/ui/components/tooltip";
import { cn } from "@workspace/ui/lib/utils";
import {
  BookOpenTextIcon,
  Building2Icon,
  ChevronLeft,
  ChevronRight,
  InfoIcon,
  LanguagesIcon,
  MapIcon,
  MapPinnedIcon,
  MessageCircleQuestionIcon,
  NotebookPenIcon,
  RefreshCwIcon,
  UserCogIcon,
  UsersRoundIcon,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { orpc } from "@/api/orpc";
import { env } from "@/config/env";
import { MapsProvider } from "@/context/maps-provider";
import { Main } from "@/layout/main";
import { withDashboardPaginationDefaults } from "@/lib/pagination";
import { fDateTime } from "@/utils/format-time";

export const Route = createFileRoute("/_authenticated/")({
  validateSearch: (search) => ActionLogSchemas.list.request.parse(withDashboardPaginationDefaults(search)),
  loaderDeps: ({ search }) => search,
  loader: async ({ deps, context: { orpc, queryClient } }) => {
    await Promise.all([
      queryClient.ensureQueryData(orpc.logs.list.queryOptions({ input: deps })),
      queryClient.ensureQueryData(orpc.admin.dashboard.summary.queryOptions()),
      queryClient.ensureQueryData(orpc.admin.dashboard.activity.queryOptions({ input: { range: "30d" } })),
    ]);
  },
  component: RouteComponent,
});

function RouteComponent() {
  return (
    <Main>
      <div className="flex flex-col gap-6">
        <DashboardStats />
        <ContentActivityChart />
        <MapSection />
        <RecentActivity />
      </div>
    </Main>
  );
}

type LanguageKey = "en" | "ar" | "tr" | "fa";
type TranslationStats = {
  total: number;
  byLanguage: Record<LanguageKey, { total: number; published?: number }>;
};
type DashboardDetail = { label: string; value: number; tooltip?: string };
type ContentSummaryCard = {
  title: string;
  total: number;
  icon: typeof Building2Icon;
  details: DashboardDetail[];
  translations: TranslationStats;
};

const dashboardLanguages: Array<{ key: LanguageKey; label: string; shortLabel: string }> = [
  { key: "en", label: "English", shortLabel: "EN" },
  { key: "ar", label: "Arabic", shortLabel: "AR" },
  { key: "tr", label: "Turkish", shortLabel: "TR" },
  { key: "fa", label: "Persian", shortLabel: "FA" },
];

function DashboardStats() {
  const { data } = useSuspenseQuery(orpc.admin.dashboard.summary.queryOptions());
  const contentCards: ContentSummaryCard[] = [
    {
      title: "Projects",
      total: data.projects.total,
      icon: Building2Icon,
      details: [
        {
          label: "Active",
          value: data.projects.active,
          tooltip:
            "Projects that are currently active. They may be public or hidden depending on their publishing settings and translations.",
        },
        {
          label: "Archived",
          value: data.projects.archived,
          tooltip: "Projects retained in the dashboard archive and excluded from the active project catalog.",
        },
        { label: "Public", value: data.projects.visible },
        {
          label: "Hidden",
          value: data.projects.hidden,
          tooltip:
            "Active projects that are not visible to visitors because publishing is disabled or no translation is published.",
        },
      ],
      translations: data.translations.projects,
    },
    {
      title: "Articles",
      total: data.blogs.total,
      icon: BookOpenTextIcon,
      details: [
        { label: "Published", value: data.blogs.published },
        { label: "Unpublished", value: data.blogs.unpublished },
      ],
      translations: data.translations.blogs,
    },
    {
      title: "Cities",
      total: data.content.cities,
      icon: MapIcon,
      details: [],
      translations: data.translations.cities,
    },
    {
      title: "Districts",
      total: data.content.districts,
      icon: MapPinnedIcon,
      details: [],
      translations: data.translations.districts,
    },
  ];
  const supportingCards = [
    {
      title: "Project drafts",
      total: data.projects.drafts,
      icon: NotebookPenIcon,
      details: [],
    },
    {
      title: "FAQ",
      total: data.content.faqs,
      icon: MessageCircleQuestionIcon,
      details: [
        { label: "Published", value: data.content.publishedFaqs },
        { label: "Unpublished", value: Math.max(data.content.faqs - data.content.publishedFaqs, 0) },
      ],
    },
    {
      title: "Customers",
      total: data.users.customers + data.users.deletedCustomers,
      icon: UsersRoundIcon,
      details: [
        { label: "Active", value: data.users.customers },
        { label: "Deleted", value: data.users.deletedCustomers },
        { label: "Saved favorites", value: data.users.favorites },
      ],
    },
    {
      title: "Dashboard users",
      total: data.users.staff,
      icon: UserCogIcon,
      details: [{ label: "Customers", value: data.users.customers }],
    },
  ];

  return (
    <section aria-label="Dashboard overview" className="space-y-6">
      <div className="space-y-1">
        <h1 className="font-semibold text-2xl tracking-tight">Overview</h1>
        <p className="text-muted-foreground text-sm">A live summary of content, translations, publishing, and users.</p>
      </div>

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        {contentCards.map((card) => (
          <ContentCard key={card.title} card={card} />
        ))}
      </div>

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {supportingCards.map((card) => (
          <Card key={card.title} className="overflow-hidden">
            <CardHeader className="flex flex-row items-center justify-between gap-3 pb-2">
              <CardTitle className="text-muted-foreground text-sm">{card.title}</CardTitle>
              <div className="rounded-lg bg-primary/10 p-2 text-primary">
                <card.icon className="size-4" />
              </div>
            </CardHeader>
            <CardContent>
              <div className="font-semibold text-3xl tabular-nums">{card.total.toLocaleString()}</div>
              <DetailsList details={card.details} />
            </CardContent>
          </Card>
        ))}
      </div>
    </section>
  );
}

function ContentCard({ card }: { card: ContentSummaryCard }) {
  return (
    <Card className="overflow-hidden">
      <CardHeader className="flex flex-row items-center justify-between gap-3 pb-2">
        <CardTitle className="text-muted-foreground text-sm">{card.title}</CardTitle>
        <div className="rounded-lg bg-primary/10 p-2 text-primary">
          <card.icon className="size-4" />
        </div>
      </CardHeader>
      <CardContent>
        <div className="font-semibold text-3xl tabular-nums">{card.total.toLocaleString()}</div>
        <DetailsList details={card.details} />
        <div className="my-4 border-t" />
        <TranslationBreakdown entityTotal={card.total} data={card.translations} />
      </CardContent>
    </Card>
  );
}

function DetailsList({ details }: { details: DashboardDetail[] }) {
  if (details.length === 0) return null;
  return (
    <div className="mt-4 space-y-2 text-sm">
      {details.map((detail) => (
        <div key={detail.label} className="flex items-center justify-between gap-3 border-t pt-2 first:border-0">
          <span className="flex items-center gap-1.5 text-muted-foreground">
            {detail.label}
            {detail.tooltip && <InfoTooltip label={`${detail.label} definition`}>{detail.tooltip}</InfoTooltip>}
          </span>
          <span className="font-medium tabular-nums">{detail.value.toLocaleString()}</span>
        </div>
      ))}
    </div>
  );
}

function TranslationBreakdown({ entityTotal, data }: { entityTotal: number; data: TranslationStats }) {
  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2">
          <LanguagesIcon className="size-4 text-primary" />
          <div>
            <div className="flex items-center gap-1.5 font-medium text-sm">
              Translations
              <InfoTooltip label="Translation count explanation">
                This is the sum of all available language versions. Each record can contribute up to four translations.
              </InfoTooltip>
            </div>
            <p className="text-[11px] text-muted-foreground">
              {data.total.toLocaleString()} translations across {entityTotal.toLocaleString()}{" "}
              {entityTotal === 1 ? "record" : "records"}
            </p>
          </div>
        </div>
      </div>

      <div className="space-y-3">
        {dashboardLanguages.map((language) => {
          const values = data.byLanguage[language.key];
          const percentage = entityTotal > 0 ? Math.min(Math.round((values.total / entityTotal) * 100), 100) : 0;

          return (
            <div key={language.key} className="space-y-1.5">
              <div className="flex items-center justify-between gap-3 text-sm">
                <div className="flex items-center gap-2">
                  <span className="inline-flex min-w-7 justify-center rounded bg-muted px-1.5 py-0.5 font-medium text-[10px]">
                    {language.shortLabel}
                  </span>
                  <span>{language.label}</span>
                </div>
                <span className="font-medium tabular-nums">
                  {values.total.toLocaleString()} <span className="text-muted-foreground text-xs">({percentage}%)</span>
                </span>
              </div>
              <div className="h-1.5 overflow-hidden rounded-full bg-muted">
                <div
                  className="h-full rounded-full bg-primary transition-[width]"
                  style={{ width: `${percentage}%` }}
                />
              </div>
              {values.published !== undefined && (
                <div className="text-right text-[11px] text-muted-foreground">
                  {values.published.toLocaleString()} published
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function InfoTooltip({ label, children }: { label: string; children: string }) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          aria-label={label}
          className="inline-flex size-4 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <InfoIcon className="size-3.5" />
        </button>
      </TooltipTrigger>
      <TooltipContent side="top" sideOffset={6} className="max-w-72 leading-relaxed">
        {children}
      </TooltipContent>
    </Tooltip>
  );
}

type ActivityRange = "7d" | "30d" | "90d" | "12m";

const activityRanges: Array<{ value: ActivityRange; label: string }> = [
  { value: "7d", label: "7 days" },
  { value: "30d", label: "30 days" },
  { value: "90d", label: "90 days" },
  { value: "12m", label: "12 months" },
];

function ContentActivityChart() {
  const [range, setRange] = useState<ActivityRange>("30d");
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const { data, isFetching } = useQuery({
    ...orpc.admin.dashboard.activity.queryOptions({ input: { range } }),
    placeholderData: (previousData) => previousData,
  });

  const chart = useMemo(() => {
    if (!data || data.points.length === 0) return null;
    const maxValue = Math.max(1, ...data.points.flatMap((point) => [point.projects, point.blogs]));
    const x = (index: number) => (data.points.length === 1 ? 500 : (index / (data.points.length - 1)) * 1000);
    const y = (value: number) => 210 - (value / maxValue) * 190;
    const path = (values: number[]) =>
      values.map((value, index) => `${index === 0 ? "M" : "L"} ${x(index)} ${y(value)}`).join(" ");
    const projectPath = path(data.points.map((point) => point.projects));
    const blogPath = path(data.points.map((point) => point.blogs));
    const labelIndexes = Array.from(
      new Set([0, 0.25, 0.5, 0.75, 1].map((part) => Math.round((data.points.length - 1) * part))),
    );

    return { maxValue, x, y, projectPath, blogPath, labelIndexes };
  }, [data]);

  const hoveredPoint = hoveredIndex !== null ? data?.points[hoveredIndex] : undefined;
  const hoveredPosition =
    hoveredIndex !== null && data && data.points.length > 1 ? (hoveredIndex / (data.points.length - 1)) * 100 : 50;

  return (
    <section aria-labelledby="content-activity-title">
      <Card className="overflow-hidden">
        <CardHeader className="gap-4 border-b bg-muted/20 md:flex md:flex-row md:items-start md:justify-between">
          <div>
            <CardTitle id="content-activity-title" className="text-lg">
              Content activity
            </CardTitle>
            <p className="mt-1 text-muted-foreground text-sm">Projects and articles added over the selected period.</p>
          </div>
          <fieldset
            className="flex flex-wrap gap-1 rounded-lg border bg-background p-1"
            aria-label="Activity time range"
          >
            {activityRanges.map((option) => (
              <Button
                key={option.value}
                type="button"
                size="sm"
                variant={range === option.value ? "default" : "ghost"}
                className="h-8 px-3"
                onClick={() => {
                  setRange(option.value);
                  setHoveredIndex(null);
                }}
              >
                {option.label}
              </Button>
            ))}
          </fieldset>
        </CardHeader>

        <CardContent className="space-y-6 pt-6">
          <div className="grid gap-3 sm:grid-cols-3">
            <ActivityMetric label="Projects added" value={data?.totals.projects ?? 0} color="navy" />
            <ActivityMetric label="Articles added" value={data?.totals.blogs ?? 0} color="gold" />
            <ActivityMetric
              label="Total additions"
              value={(data?.totals.projects ?? 0) + (data?.totals.blogs ?? 0)}
              color="neutral"
            />
          </div>

          <div className={cn("relative transition-opacity", isFetching && "opacity-65")}>
            {data && chart ? (
              <>
                <div className="relative h-64 pl-8 sm:h-72">
                  <div className="absolute inset-y-0 left-0 flex flex-col justify-between pb-8 text-[10px] text-muted-foreground tabular-nums">
                    <span>{chart.maxValue}</span>
                    <span>{Math.round(chart.maxValue / 2)}</span>
                    <span>0</span>
                  </div>
                  <div className="absolute inset-x-8 top-0 bottom-8">
                    <svg
                      aria-hidden="true"
                      className="h-full w-full overflow-visible"
                      preserveAspectRatio="none"
                      viewBox="0 0 1000 220"
                    >
                      <defs>
                        <linearGradient id="project-activity-fill" x1="0" x2="0" y1="0" y2="1">
                          <stop offset="0%" stopColor="#244467" stopOpacity="0.18" />
                          <stop offset="100%" stopColor="#244467" stopOpacity="0" />
                        </linearGradient>
                        <linearGradient id="blog-activity-fill" x1="0" x2="0" y1="0" y2="1">
                          <stop offset="0%" stopColor="#d4ad52" stopOpacity="0.2" />
                          <stop offset="100%" stopColor="#d4ad52" stopOpacity="0" />
                        </linearGradient>
                      </defs>
                      {[20, 67.5, 115, 162.5, 210].map((lineY) => (
                        <line
                          key={lineY}
                          x1="0"
                          x2="1000"
                          y1={lineY}
                          y2={lineY}
                          stroke="currentColor"
                          className="text-border"
                        />
                      ))}
                      <path d={`${chart.projectPath} L 1000 210 L 0 210 Z`} fill="url(#project-activity-fill)" />
                      <path d={`${chart.blogPath} L 1000 210 L 0 210 Z`} fill="url(#blog-activity-fill)" />
                      <path
                        d={chart.projectPath}
                        fill="none"
                        stroke="#244467"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="4"
                      />
                      <path
                        d={chart.blogPath}
                        fill="none"
                        stroke="#d4ad52"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="4"
                      />
                    </svg>

                    <div className="absolute inset-0 flex">
                      {data.points.map((point, index) => (
                        <button
                          key={point.bucket}
                          type="button"
                          className="h-full flex-1 cursor-crosshair focus-visible:outline-none"
                          aria-label={`${formatActivityBucket(point.bucket, data.granularity)}: ${point.projects} projects, ${point.blogs} articles`}
                          onMouseEnter={() => setHoveredIndex(index)}
                          onMouseLeave={() => setHoveredIndex(null)}
                          onFocus={() => setHoveredIndex(index)}
                          onBlur={() => setHoveredIndex(null)}
                        />
                      ))}
                    </div>

                    {hoveredPoint && hoveredIndex !== null && (
                      <>
                        <div
                          className="pointer-events-none absolute inset-y-0 w-px bg-foreground/20"
                          style={{ left: `${hoveredPosition}%` }}
                        />
                        <span
                          className="pointer-events-none absolute size-2.5 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-background bg-[#244467] shadow"
                          style={{
                            left: `${hoveredPosition}%`,
                            top: `${(chart.y(hoveredPoint.projects) / 220) * 100}%`,
                          }}
                        />
                        <span
                          className="pointer-events-none absolute size-2.5 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-background bg-[#d4ad52] shadow"
                          style={{ left: `${hoveredPosition}%`, top: `${(chart.y(hoveredPoint.blogs) / 220) * 100}%` }}
                        />
                        <div
                          className="pointer-events-none absolute top-2 z-10 min-w-40 -translate-x-1/2 rounded-lg border bg-popover p-3 text-popover-foreground text-xs shadow-lg"
                          style={{ left: `${Math.min(Math.max(hoveredPosition, 10), 90)}%` }}
                        >
                          <div className="mb-2 font-medium">
                            {formatActivityBucket(hoveredPoint.bucket, data.granularity)}
                          </div>
                          <div className="flex items-center justify-between gap-4">
                            <span className="flex items-center gap-2 text-muted-foreground">
                              <span className="size-2 rounded-full bg-[#244467]" /> Projects
                            </span>
                            <strong>{hoveredPoint.projects}</strong>
                          </div>
                          <div className="mt-1 flex items-center justify-between gap-4">
                            <span className="flex items-center gap-2 text-muted-foreground">
                              <span className="size-2 rounded-full bg-[#d4ad52]" /> Articles
                            </span>
                            <strong>{hoveredPoint.blogs}</strong>
                          </div>
                        </div>
                      </>
                    )}
                  </div>

                  <div className="absolute inset-x-8 bottom-0 flex justify-between text-[10px] text-muted-foreground sm:text-xs">
                    {chart.labelIndexes.map((index) => (
                      <span key={data.points[index].bucket}>
                        {formatActivityBucket(data.points[index].bucket, data.granularity)}
                      </span>
                    ))}
                  </div>
                </div>

                <div className="mt-4 flex flex-wrap items-center justify-center gap-5 text-sm">
                  <span className="flex items-center gap-2">
                    <span className="h-0.5 w-6 bg-[#244467]" /> Projects
                  </span>
                  <span className="flex items-center gap-2">
                    <span className="h-0.5 w-6 bg-[#d4ad52]" /> Articles
                  </span>
                </div>
              </>
            ) : (
              <div className="flex h-72 items-center justify-center text-muted-foreground text-sm">
                No activity data available.
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </section>
  );
}

function ActivityMetric({ label, value, color }: { label: string; value: number; color: "navy" | "gold" | "neutral" }) {
  const colorClass = {
    navy: "border-[#244467] bg-[#244467]/5",
    gold: "border-[#d4ad52] bg-[#d4ad52]/10",
    neutral: "border-muted-foreground/40 bg-muted/30",
  }[color];

  return (
    <div className={cn("rounded-lg border-l-4 p-4", colorClass)}>
      <div className="text-muted-foreground text-xs">{label}</div>
      <div className="mt-1 font-semibold text-2xl tabular-nums">{value.toLocaleString()}</div>
    </div>
  );
}

function formatActivityBucket(bucket: string, granularity: "day" | "week" | "month") {
  const date = new Date(`${bucket}T00:00:00Z`);
  return new Intl.DateTimeFormat("en", {
    month: "short",
    ...(granularity === "month" ? { year: "2-digit" as const } : { day: "numeric" as const }),
  }).format(date);
}

function RecentActivity() {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const { data } = useSuspenseQuery(orpc.logs.list.queryOptions({ input: search }));

  const setPage = (page: number) => {
    void navigate({ search: (previous) => ({ ...previous, page }) });
  };

  return (
    <section className="flex flex-col gap-3">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h2 className="font-semibold text-lg">Recent Activity</h2>
          <p className="text-muted-foreground text-sm">{data.totalItems} logged actions</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant="outline"
            size="icon"
            disabled={!data.hasPreviousPage}
            onClick={() => data.previousPage && setPage(data.previousPage)}
          >
            <ChevronLeft className="h-4 w-4" />
            <span className="sr-only">Previous page</span>
          </Button>
          <div className="min-w-20 text-center text-muted-foreground text-sm">
            {data.currentPage} / {Math.max(data.totalPageNumbers, 1)}
          </div>
          <Button
            type="button"
            variant="outline"
            size="icon"
            disabled={!data.hasNextPage}
            onClick={() => data.nextPage && setPage(data.nextPage)}
          >
            <ChevronRight className="h-4 w-4" />
            <span className="sr-only">Next page</span>
          </Button>
        </div>
      </div>

      <div className="overflow-hidden rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Time</TableHead>
              <TableHead>Actor</TableHead>
              <TableHead>Action</TableHead>
              <TableHead>Entity</TableHead>
              <TableHead>Target</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.items.length > 0 ? (
              data.items.map((log) => (
                <TableRow key={log.id}>
                  <TableCell className="whitespace-nowrap">{fDateTime(log.createdAt)}</TableCell>
                  <TableCell>
                    <div className="font-medium">{log.actorName}</div>
                    <div className="text-muted-foreground text-xs">{log.actorEmail}</div>
                  </TableCell>
                  <TableCell>{getActionLabel(log.action)}</TableCell>
                  <TableCell>{getEntityLabel(log.entityType)}</TableCell>
                  <TableCell className="font-medium">{log.entityName}</TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={5} className="h-24 text-center">
                  No activity yet.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </section>
  );
}

function getActionLabel(action: ActionLog["action"]) {
  const labels = {
    create: "Created",
    update: "Updated",
    delete: "Deleted",
  };

  return labels[action];
}

function getEntityLabel(entityType: ActionLog["entityType"]) {
  const labels = {
    project: "Project",
    district: "District",
    blog: "Blog",
    city: "City",
    feature: "Feature",
    distance: "Distance",
    plan: "Plan",
    staff: "Dashboard user",
  };

  return labels[entityType];
}

export function MapSection({ className }: { className?: string }) {
  const [mapFailed, setMapFailed] = useState(false);
  const handleMapError = useCallback(() => setMapFailed(true), []);

  if (mapFailed) {
    return (
      <section
        className={cn(
          "flex min-h-80 flex-col items-center justify-center rounded-lg border bg-muted/30 p-8 text-center",
          className,
        )}
      >
        <MapPinnedIcon className="mb-4 size-10 text-muted-foreground" />
        <h2 className="font-semibold text-lg">The project map is unavailable</h2>
        <p className="mt-2 max-w-xl text-muted-foreground text-sm">
          Check the dashboard Google Maps key, allowed referrers, Maps JavaScript API, and billing configuration.
        </p>
        <Button type="button" variant="outline" className="mt-4" onClick={() => setMapFailed(false)}>
          <RefreshCwIcon /> Try again
        </Button>
      </section>
    );
  }

  return (
    <section className="space-y-3">
      <div>
        <h2 className="font-semibold text-lg">Project locations</h2>
        <p className="text-muted-foreground text-sm">Only projects with valid coordinates are displayed.</p>
      </div>
      <div className="overflow-hidden rounded-lg border">
        <MapsProvider onError={handleMapError}>
          <GoogleMap
            className={cn("h-[420px] w-full", className)}
            mapId={env.VITE_MAP_ID}
            defaultCenter={{ lat: 41.02936, lng: 28.807263 }}
            defaultZoom={10}
            disableDefaultUI
            minZoom={6}
            restriction={{
              strictBounds: false,
              latLngBounds: {
                north: 43.364,
                south: 34.55,
                east: 48.647,
                west: 21.835,
              },
            }}
          />
          <Markers />
        </MapsProvider>
      </div>
    </section>
  );
}

function Markers() {
  const { data: projects } = useQuery(orpc.admin.projects.listMap.queryOptions({ input: {} }));

  const locations = useMemo(
    () =>
      (projects ?? [])
        .map((project) => ({
          lat: Number(project.location?.lat),
          lng: Number(project.location?.lng),
          key: project.id,
          code: project.code,
        }))
        .filter(
          (point) =>
            Number.isFinite(point.lat) &&
            Number.isFinite(point.lng) &&
            point.lat !== 0 &&
            point.lng !== 0 &&
            Math.abs(point.lat) <= 90 &&
            Math.abs(point.lng) <= 180,
        ),
    [projects],
  );
  const map = useMap();
  const [markers, setMarkers] = useState<{ [key: string]: Marker }>({});
  const clusterer = useRef<MarkerClusterer | null>(null);

  useEffect(() => {
    if (!map || locations.length === 0) return;
    if (!clusterer.current) {
      clusterer.current = new MarkerClusterer({ map });
    }

    if (locations.length === 1) {
      map.setCenter(locations[0]);
      map.setZoom(13);
      return;
    }
    const bounds = new google.maps.LatLngBounds();
    for (const point of locations) bounds.extend(point);
    map.fitBounds(bounds);
  }, [locations, map]);

  useEffect(() => {
    clusterer.current?.clearMarkers();
    clusterer.current?.addMarkers(Object.values(markers));
  }, [markers]);

  const setMarkerRef = (marker: Marker | null, key: string) => {
    if (marker && markers[key]) return;
    if (!marker && !markers[key]) return;

    setMarkers((prev) => {
      if (marker) return { ...prev, [key]: marker };

      const newMarkers = { ...prev };
      delete newMarkers[key];
      return newMarkers;
    });
  };

  return (
    <>
      {locations.map((point) => (
        <AdvancedMarker position={point} key={point.key} ref={(marker) => setMarkerRef(marker, point.key)}>
          <Link to="/projects/$projectId/update" params={{ projectId: point.key }} className="relative">
            <div className="rounded-lg border-black/50 bg-background px-3 py-1 font-semibold text-primary text-sm shadow-md">
              {point.code}
            </div>

            <div className="mx-auto -mt-1.5 h-3 w-3 rotate-45 border-black/50 bg-background" />
          </Link>
        </AdvancedMarker>
      ))}
    </>
  );
}
